import { Injectable } from '@angular/core';

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class TableSortingService {

  /**
   * Sort an array of objects by a specific key
   * @param data Array of objects to sort
   * @param key Property key to sort by
   * @param direction Sort direction
   * @returns Sorted array
   */
  sort<T>(data: T[], key: string, direction: 'asc' | 'desc'): T[] {
    if (!data || data.length === 0) {
      return data;
    }

    return [...data].sort((a: any, b: any) => {
      const aValue = this.getNestedValue(a, key);
      const bValue = this.getNestedValue(b, key);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return direction === 'asc' ? 1 : -1;
      if (bValue == null) return direction === 'asc' ? -1 : 1;

      // Handle different data types
      const comparison = this.compareValues(aValue, bValue);
      return direction === 'asc' ? comparison : -comparison;
    });
  }

  /**
   * Get nested property value using dot notation
   * @param obj Object to get value from
   * @param key Property key (supports dot notation like 'user.name')
   * @returns Property value
   */
  private getNestedValue(obj: any, key: string): any {
    return key.split('.').reduce((o, k) => (o || {})[k], obj);
  }

  /**
   * Compare two values of potentially different types
   * @param a First value
   * @param b Second value
   * @returns Comparison result (-1, 0, 1)
   */
  private compareValues(a: any, b: any): number {
    // Convert to string for comparison if not numbers or dates
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() - b.getTime();
    }

    // Handle boolean values
    if (typeof a === 'boolean' && typeof b === 'boolean') {
      return a === b ? 0 : a ? 1 : -1;
    }

    // Convert to strings and compare (case-insensitive)
    const aStr = String(a).toLowerCase();
    const bStr = String(b).toLowerCase();

    if (aStr < bStr) return -1;
    if (aStr > bStr) return 1;
    return 0;
  }

  /**
   * Toggle sort direction
   * @param currentDirection Current sort direction
   * @returns New sort direction
   */
  toggleDirection(currentDirection: 'asc' | 'desc'): 'asc' | 'desc' {
    return currentDirection === 'asc' ? 'desc' : 'asc';
  }
}
